import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { AuthApi } from '@up2stay/up2promo-api-client';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { shellPaths } from '@/core/router/app-paths';

import {
  getAuthSessionCache,
  increaseAuthSessionRetryDelay,
  markAuthSessionValidated,
  recordAuthSessionFailure,
  resetAuthSessionCache,
  resetAuthSessionFailureCount,
  resetAuthSessionRetryDelay,
  setAuthSessionMeRequestInProgress,
} from '@/core/hooks/auth-session-cache';
import { performFullLogout } from '@/core/hooks/use-logout';
import { AuthStatus, useAuthStore } from '@/core/store/auth.store';

type HttpLikeError = {
  response?: {
    status?: number;
  };
};

function isSessionRejectedStatus(status: number | undefined): boolean {
  return status === 401;
}

/**
 * Validates the auth session against the backend on protected routes.
 * On refresh-token rejection (401 with no recoverable refresh token) it clears auth state;
 * 403/5xx/network errors keep the session. Navigation to /auth is handled by withAuth.
 */
export function useAuthSession() {
  const setUser = useAuthStore((state) => state.setUser);
  const setAuthStatus = useAuthStore((state) => state.setAuthStatus);
  const hydrated = useAuthStore((state) => state.hydrated);
  const location = useLocation();

  const shouldValidateSession =
    !location.pathname.startsWith('/auth') &&
    !location.pathname.startsWith(shellPaths.moreConfirmNewEmail) &&
    !location.pathname.startsWith('/workspace/invitation');

  useEffect(() => {
    if (!hydrated || !shouldValidateSession) {
      return;
    }

    let cancelled = false;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    let appStateListener: Awaited<ReturnType<typeof App.addListener>> | null =
      null;

    const clearAuth = () => {
      resetAuthSessionCache();
      void performFullLogout();
    };

    const scheduleRetry = () => {
      const delay = increaseAuthSessionRetryDelay();
      retryTimeout = setTimeout(() => {
        if (!cancelled) {
          void syncSession();
        }
      }, delay);
    };

    const markDegradedAndRetry = () => {
      setAuthStatus(AuthStatus.OFFLINE);
      // Track failures but keep retrying with backoff — giving up left the
      // network-error banner stuck even after the API recovered.
      recordAuthSessionFailure();
      scheduleRetry();
    };

    const syncSession = async () => {
      const authState = useAuthStore.getState();
      const accessToken = authState.tokens?.accessToken;
      const refreshToken = authState.tokens?.refreshToken;
      const { meRequestInProgress, validatedAccessToken } =
        getAuthSessionCache();

      if (!accessToken) {
        if (refreshToken) {
          markDegradedAndRetry();
          return;
        }
        if (authState.authStatus !== AuthStatus.UNAUTHENTICATED) {
          clearAuth();
        }
        return;
      }

      if (
        meRequestInProgress ||
        (validatedAccessToken === accessToken &&
          authState.authStatus === AuthStatus.AUTHENTICATED)
      ) {
        return;
      }

      setAuthSessionMeRequestInProgress(true);

      try {
        const response = await AuthApi.me();
        if (cancelled) {
          return;
        }

        markAuthSessionValidated(accessToken);
        setUser(response.data);
        setAuthStatus(AuthStatus.AUTHENTICATED);
      } catch (error) {
        if (cancelled) {
          return;
        }

        const status =
          error && typeof error === 'object' && 'response' in error
            ? (error as HttpLikeError).response?.status
            : undefined;

        if (isSessionRejectedStatus(status)) {
          const currentTokens = useAuthStore.getState().tokens;
          if (currentTokens?.refreshToken) {
            markDegradedAndRetry();
            return;
          }
          clearAuth();
          return;
        }

        markDegradedAndRetry();
      } finally {
        setAuthSessionMeRequestInProgress(false);
      }
    };

    const recoverAndSync = () => {
      if (cancelled) {
        return;
      }
      if (retryTimeout) {
        clearTimeout(retryTimeout);
        retryTimeout = null;
      }
      resetAuthSessionFailureCount();
      resetAuthSessionRetryDelay();
      void syncSession();
    };

    void syncSession();

    const handleOnline = () => {
      recoverAndSync();
    };
    window.addEventListener('online', handleOnline);

    if (Capacitor.isNativePlatform()) {
      void App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          recoverAndSync();
        }
      }).then((listener) => {
        if (cancelled) {
          void listener.remove();
          return;
        }
        appStateListener = listener;
      });
    }

    return () => {
      cancelled = true;
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      window.removeEventListener('online', handleOnline);
      if (appStateListener) {
        void appStateListener.remove();
      }
    };
  }, [hydrated, setAuthStatus, setUser, shouldValidateSession]);
}

export { resetAuthSessionCache } from '@/core/hooks/auth-session-cache';
